import { RunnerBoyClient } from '@runnerboy/api-client';
import type { AuthUserDto } from '@runnerboy/types';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';

export const client = new RunnerBoyClient({ baseUrl: '', app: 'STORE' });

export interface MyStore {
  id: string;
  name: string;
  status: string;
  operationalState: 'OPEN' | 'PAUSED' | 'CLOSED';
  locality: string;
  isDemo: boolean;
  avgPrepMinutes: number;
  role: string;
}

interface Ctx {
  ready: boolean;
  user: AuthUserDto | null;
  stores: MyStore[];
  store: MyStore | null;
  selectStore(id: string): void;
  reloadStores(): Promise<void>;
  socket: Socket | null;
  connected: boolean;
}

const C = createContext<Ctx | null>(null);

export function StoreSessionProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<AuthUserDto | null>(null);
  const [stores, setStores] = useState<MyStore[]>([]);
  const [storeId, setStoreId] = useState<string | null>(() => localStorage.getItem('rb.store.selected'));
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  const reloadStores = async () => {
    const s = await client.get<MyStore[]>('/store/stores');
    setStores(s);
    if (!s.find((x) => x.id === storeId)) setStoreId(s[0]?.id ?? null);
  };

  useEffect(() => {
    client.opts.onSessionChange = setUser;
    void client.restore().finally(() => setReady(true));
  }, []);
  useEffect(() => {
    if (user) void reloadStores().catch(() => undefined);
    else setStores([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user || !storeId) return;
    localStorage.setItem('rb.store.selected', storeId);
    const s = io({ path: '/socket.io', auth: (cb) => cb({ token: client.accessToken }), transports: ['websocket', 'polling'] });
    s.on('connect', () => {
      setConnected(true);
      s.emit('store.subscribe', { storeId });
    });
    s.on('disconnect', () => setConnected(false));
    s.on('auth.error', () => void client.restore().then(() => s.connect()));
    setSocket(s);
    return () => {
      s.disconnect();
      setSocket(null);
    };
  }, [user, storeId]);

  const value = useMemo<Ctx>(
    () => ({ ready, user, stores, store: stores.find((s) => s.id === storeId) ?? null, selectStore: setStoreId, reloadStores, socket, connected }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, user, stores, storeId, socket, connected],
  );
  return <C.Provider value={value}>{children}</C.Provider>;
}

export function useStoreSession() {
  const v = useContext(C);
  if (!v) throw new Error('StoreSessionProvider missing');
  return v;
}

/** Repeating two-tone alert for new orders (Web Audio; must be unlocked by a user gesture). */
export class OrderAlarm {
  private ctx: AudioContext | null = null;
  private timer: number | null = null;
  unlock() {
    if (!this.ctx) this.ctx = new AudioContext();
    void this.ctx.resume();
  }
  get unlocked() {
    return this.ctx?.state === 'running';
  }
  private beep() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const [i, f] of [880, 660, 880].entries()) {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.frequency.value = f;
      o.type = 'square';
      g.gain.setValueAtTime(0.0001, now + i * 0.22);
      g.gain.exponentialRampToValueAtTime(0.25, now + i * 0.22 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.22 + 0.2);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.22);
      o.stop(now + i * 0.22 + 0.21);
    }
    navigator.vibrate?.([200, 100, 200]);
  }
  start() {
    if (this.timer !== null) return;
    this.beep();
    this.timer = window.setInterval(() => this.beep(), 2500);
  }
  stop() {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }
}
export const alarm = new OrderAlarm();
